import {FC, useEffect, useState} from 'react'
import {
  getAdminExpertProfiles,
  getMemberEngagements,
} from '../../../services/memberExpertiseService'
import {Expert, formatRate, ownerTypeLabel} from '../../bookExpert/expertDirectory'
import {
  Engagement,
  expertDisplay,
  formatMoney,
  statusColor,
  statusLabel,
} from '../../bookExpert/engagements'
import {pickMemberExpertProfile, splitEngagementsByRole, toArray} from '../utils/memberExpertise'

type Props = {userId: number}

const StatusBadge: FC<{status?: string}> = ({status}) => (
  <span className='badge' style={{backgroundColor: statusColor(status), color: '#fff'}}>
    {statusLabel(status)}
  </span>
)

// A small engagements table; `counterparty` renders the OTHER party for the member being viewed.
const EngagementTable: FC<{
  rows: Engagement[]
  counterpartyHeader: string
  counterparty: (e: Engagement) => string
}> = ({rows, counterpartyHeader, counterparty}) => (
  <table className='admin-community__table'>
    <thead>
      <tr>
        <th>{counterpartyHeader}</th>
        <th>Scope</th>
        <th>Amount</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((e) => (
        <tr key={e.id}>
          <td>{counterparty(e)}</td>
          <td className='text-truncate' style={{maxWidth: 320}}>
            {e.scope || '—'}
          </td>
          <td>{formatMoney(e.agreedAmount, e.currency)}</td>
          <td>
            <StatusBadge status={e.status} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
)

// WP-X7 — member expertise tab (admin member-detail): the member's ExpertProfile (if any) + their
// engagements as client and as expert. Both reads are BLOCKED on deploy (X4 expert profiles; an
// admin by-member engagements endpoint) — each section degrades gracefully with a clear note.
const MemberExpertiseTab: FC<Props> = ({userId}) => {
  const [profile, setProfile] = useState<Expert | null>(null)
  const [profileBlocked, setProfileBlocked] = useState(false)
  const [profileLoading, setProfileLoading] = useState(true)

  const [engagements, setEngagements] = useState<Engagement[]>([])
  const [engagementsBlocked, setEngagementsBlocked] = useState(false)
  const [engagementsLoading, setEngagementsLoading] = useState(true)

  useEffect(() => {
    let active = true
    getAdminExpertProfiles()
      .then((data) => active && setProfile(pickMemberExpertProfile(data, userId)))
      .catch(() => active && setProfileBlocked(true))
      .finally(() => active && setProfileLoading(false))

    getMemberEngagements(userId)
      .then((data) => active && setEngagements(toArray<Engagement>(data)))
      .catch(() => active && setEngagementsBlocked(true))
      .finally(() => active && setEngagementsLoading(false))

    return () => {
      active = false
    }
  }, [userId])

  const {asClient, asExpert} = splitEngagementsByRole(engagements, userId)

  return (
    <div className='member-expertise-tab'>
      {/* Expert profile */}
      <div className='card p-6 mb-6'>
        <h3 className='mb-4'>Expert profile</h3>
        {profileLoading ? (
          <div className='text-muted'>Loading…</div>
        ) : profileBlocked ? (
          <div className='text-muted'>
            Expert profile is unavailable — blocked on the expertise X4 deploy.
          </div>
        ) : !profile ? (
          <div className='text-muted'>This member is not registered as an expert.</div>
        ) : (
          <>
            <div className='d-flex flex-wrap align-items-center gap-4 mb-3'>
              {ownerTypeLabel(profile.ownerType) && (
                <span className='badge badge-light-secondary'>
                  {ownerTypeLabel(profile.ownerType)}
                </span>
              )}
              {(profile as any).isListed ? (
                <span className='badge badge-light-success'>Listed</span>
              ) : (
                <span className='badge badge-light-warning'>Not listed</span>
              )}
              {(profile as any).active === false && (
                <span className='badge badge-light-danger'>Inactive</span>
              )}
              <span className='fw-bold ms-auto'>
                {formatRate(profile.listedRate, profile.currency, profile.rateType)}
              </span>
            </div>
            {profile.headline && <div className='text-gray-800 mb-2'>{profile.headline}</div>}
            <div className='d-flex flex-wrap gap-2 mb-2'>
              {(profile.expertiseAreas ?? []).map((a) => (
                <span key={a.id} className='badge badge-light-primary'>
                  {a.name}
                </span>
              ))}
            </div>
            {profile.bio && (
              <div className='text-gray-700' style={{whiteSpace: 'pre-line'}}>
                {profile.bio}
              </div>
            )}
          </>
        )}
      </div>

      {/* Engagements */}
      <div className='card p-6'>
        <h3 className='mb-4'>Engagements</h3>
        {engagementsLoading ? (
          <div className='text-muted'>Loading…</div>
        ) : engagementsBlocked ? (
          <div className='text-muted'>
            A member’s engagements aren’t available here yet — this needs an admin “engagements by
            member” endpoint (blocked on backend). The engagement API itself is live.
          </div>
        ) : engagements.length === 0 ? (
          <div className='text-muted'>No engagements for this member.</div>
        ) : (
          <>
            {asClient.length > 0 && (
              <div className='mb-6'>
                <div className='text-muted fs-8 text-uppercase mb-2'>As client</div>
                <EngagementTable
                  rows={asClient}
                  counterpartyHeader='Expert'
                  counterparty={(e) => expertDisplay(e)}
                />
              </div>
            )}
            {asExpert.length > 0 && (
              <div>
                <div className='text-muted fs-8 text-uppercase mb-2'>As expert</div>
                <EngagementTable
                  rows={asExpert}
                  counterpartyHeader='Client'
                  counterparty={(e) => e.client?.name || '—'}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default MemberExpertiseTab
